import React, { ReactNode, useState } from 'react';
import Icon from '~/UI/Icon';
import { ButtonCancel } from '~/pages/IRPDetail/src/components/ButtonCancel';
import { TailwindModal } from '../../atoms/TailwindModal';
import { TailwindTooltip } from '../../atoms/TailwindTooltip';

export interface RefreshProductListActionProps {
	/** Name of the product group/list shown in the confirmation modal title. */
	groupName: string;
	/** Explanation shown in both the tooltip and the confirmation modal, localized per module. */
	description: ReactNode;
	/** Invoked once the user confirms the refresh in the modal. */
	onConfirm(): void;
}

const refreshButtonClassName =
	'inline-block px-2.5 py-1.5 rounded text-sm leading-snug font-semibold text-white bg-red-600 align-middle transition hover:bg-red-800';

/**
 * Tooltip + "Refresh product list" trigger button with a confirmation modal.
 * Used to reset module-specific calculation settings (prices, validities, product selection)
 * back to their recalculated defaults for a product list.
 *
 * @returns {JSX.Element} The trigger button (with tooltip) and, once clicked, a confirmation modal.
 */
export function RefreshProductListAction({ groupName, description, onConfirm }: RefreshProductListActionProps) {
	const [isConfirming, setIsConfirming] = useState(false);

	return (
		<>
			<TailwindTooltip
				reference={<Icon name='violet-question-mark' />}
				displayClass='inline-block -mt-px mr-2 h-4 align-middle'
				position='left'
			>
				<p className='leading-snug'>{description}</p>
			</TailwindTooltip>
			<button
				type='button'
				onClick={() => {
					setIsConfirming(true);
				}}
				className={refreshButtonClassName}
			>
				Refresh product list
			</button>
			{isConfirming ? (
				<TailwindModal
					title={`Refresh product list: ${groupName}`}
					footer={
						<div className='w-full flex items-center justify-center'>
							<ButtonCancel
								onClick={() => {
									setIsConfirming(false);
								}}
							>
								Close
							</ButtonCancel>
							<button className={refreshButtonClassName}>Refresh product list</button>
						</div>
					}
					onClose={() => {
						setIsConfirming(false);
					}}
					onSubmit={() => {
						setIsConfirming(false);
						onConfirm();
					}}
				>
					<p className='leading-snug text-center'>{description}</p>
				</TailwindModal>
			) : null}
		</>
	);
}
